// Role-Based Access Control (RBAC) for QKey
// Implements security recommendations from July 2025 audit

export enum UserRole {
  ADMIN = 'admin',
  USER = 'user',
  VIEWER = 'viewer'
}

export enum Permission {
  // Wallet operations
  CREATE_WALLET = 'create_wallet',
  VIEW_WALLET = 'view_wallet',
  DELETE_WALLET = 'delete_wallet',
  EXPORT_KEYS = 'export_keys',
  
  // Transaction operations
  CREATE_TRANSACTION = 'create_transaction',
  SIGN_TRANSACTION = 'sign_transaction',
  BROADCAST_TRANSACTION = 'broadcast_transaction',
  
  // Admin operations
  MANAGE_USERS = 'manage_users',
  VIEW_AUDIT_LOGS = 'view_audit_logs',
  UPDATE_SETTINGS = 'update_settings',
  
  // Quantum operations
  GENERATE_QUANTUM_KEYS = 'generate_quantum_keys',
  REGISTER_QD3 = 'register_qd3',
  USE_PQP = 'use_pqp'
}

// Role-Permission mapping
const rolePermissions: Record<UserRole, Permission[]> = {
  [UserRole.ADMIN]: [
    // Admin has all permissions
    Permission.CREATE_WALLET,
    Permission.VIEW_WALLET,
    Permission.DELETE_WALLET,
    Permission.EXPORT_KEYS,
    Permission.CREATE_TRANSACTION,
    Permission.SIGN_TRANSACTION,
    Permission.BROADCAST_TRANSACTION,
    Permission.MANAGE_USERS,
    Permission.VIEW_AUDIT_LOGS,
    Permission.UPDATE_SETTINGS,
    Permission.GENERATE_QUANTUM_KEYS,
    Permission.REGISTER_QD3,
    Permission.USE_PQP
  ],
  [UserRole.USER]: [
    // Regular users can perform wallet and transaction operations
    Permission.CREATE_WALLET,
    Permission.VIEW_WALLET,
    Permission.EXPORT_KEYS,
    Permission.CREATE_TRANSACTION,
    Permission.SIGN_TRANSACTION,
    Permission.BROADCAST_TRANSACTION,
    Permission.GENERATE_QUANTUM_KEYS,
    Permission.REGISTER_QD3,
    Permission.USE_PQP
  ],
  [UserRole.VIEWER]: [
    // Viewers can only view, no modifications
    Permission.VIEW_WALLET
  ]
}

export class RBAC {
  static hasPermission(role: UserRole, permission: Permission): boolean {
    const permissions = rolePermissions[role] || []
    return permissions.includes(permission)
  }
  
  static hasAnyPermission(role: UserRole, permissions: Permission[]): boolean {
    return permissions.some(permission => this.hasPermission(role, permission))
  }
  
  static hasAllPermissions(role: UserRole, permissions: Permission[]): boolean {
    return permissions.every(permission => this.hasPermission(role, permission))
  }
  
  static getRolePermissions(role: UserRole): Permission[] {
    return rolePermissions[role] || []
  }
}

// Context for React components
export interface AuthContext {
  user: {
    id: string
    role: UserRole
    mfaEnabled: boolean
  } | null
  hasPermission: (permission: Permission) => boolean
}